import React, { useState, useRef, useEffect } from 'react';

/**
 * Pye the AI Guy - Conversational AI Assistant
 * Hybrid chat + search interface (like Perplexity, ChatGPT with search)
 * Can have conversations AND search data seamlessly
 */
const PyeAIChat = ({ backendUrl, onSearch, currentFilters }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hey there! I'm **Pye the AI Guy** 👋\n\nI can help you search through inspection data or just chat! Try asking me:\n\n• *'How many submissions does Matt have?'*\n• *'Show me approved inspections in Georgia'*\n• *'How are you doing?'*\n\nWhat can I help you with?",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isThinking) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setIsThinking(true);

    try {
      // Build conversation context from recent messages
      const recentContext = messages.slice(-3).map(m => 
        `${m.role === 'user' ? 'User' : 'Pye'}: ${m.content}`
      ).join('\n');

      // Call Pye chat endpoint
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/pye-chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          query: userMessage,
          context: recentContext
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Chat failed');
      }

      let aiResponse = '';
      let hasDataQuery = false;
      let resultCount = 0;
      let aiFilters = null;

      if (data.type === 'data_query') {
        // DATA SEARCH - Execute search and show results
        aiFilters = data.filters;
        
        const searchParams = new URLSearchParams({
          ...currentFilters,
          q: userMessage,
          search_mode: 'ai',
          limit: '100'
        });

        const searchToken = localStorage.getItem('token');
        const searchResponse = await fetch(`${backendUrl}/api/admin/inspections?${searchParams.toString()}`, {
          headers: {
            ...(searchToken && { 'Authorization': `Bearer ${searchToken}` })
          }
        });
        const searchData = await searchResponse.json();
        
        resultCount = searchData.inspections?.length || 0;
        hasDataQuery = true;

        const filterSummary = Object.entries(aiFilters)
          .map(([key, value]) => `${key.replace(/_/g, ' ')}: **${value}**`)
          .join(', ');

        aiResponse = `I found **${resultCount} inspection${resultCount !== 1 ? 's' : ''}** for you!\n\n`;
        aiResponse += `🔍 ${filterSummary}\n\n`;
        
        if (resultCount > 0) {
          aiResponse += `Check out the results in the table below! Want me to narrow it down more?`;
          
          if (onSearch) {
            onSearch(userMessage, aiFilters, searchData.inspections);
          }
        } else {
          aiResponse += `Hmm, no matches. Try asking differently or search for something else!`;
        }
      } else {
        // CONVERSATIONAL - Use LLM-generated response
        aiResponse = data.message || "Hey! I'm here to help. What do you want to know?";
      }

      // Add AI response to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        hasData: hasDataQuery,
        resultCount: hasDataQuery ? resultCount : null,
        filters: hasDataQuery ? aiFilters : null,
        timestamp: new Date()
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Oops! Something went wrong: ${error.message}\n\nTry asking me something else!`,
        timestamp: new Date()
      }]);
    } finally {
      setIsThinking(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-[var(--pbfs-navy)]/60 border border-[var(--pbfs-green)]/30 rounded-xl overflow-hidden flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className="bg-[var(--pbfs-green)] p-4 border-b border-[var(--pbfs-green)]/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-[var(--pbfs-green)]">P</span>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Pye the AI Guy</h3>
            <p className="text-white/80 text-xs">Your friendly search assistant</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-[var(--pbfs-navy)]/40 to-[var(--pbfs-navy)]/60">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] p-4 rounded-lg ${
                msg.role === 'user'
                  ? 'bg-[var(--pbfs-blue)] text-white'
                  : 'bg-white/10 border border-white/20 text-white'
              }`}
            >
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-[var(--pbfs-green)] rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">P</span>
                  </div>
                  <span className="font-bold text-[var(--pbfs-green)] text-sm">Pye</span>
                </div>
              )}
              
              {/* Markdown-like formatting */}
              <div className="text-sm leading-relaxed whitespace-pre-wrap">
                {msg.content.split('\n').map((line, i) => {
                  // Bold text with **
                  const parts = line.split(/(\*\*.*?\*\*)/g);
                  return (
                    <p key={i} className={i > 0 ? 'mt-2' : ''}>
                      {parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={j} className="text-[var(--pbfs-green)]">{part.slice(2, -2)}</strong>;
                        }
                        // Italic with *
                        if (part.startsWith('*') && part.endsWith('*')) {
                          return <em key={j} className="text-white/80">{part.slice(1, -1)}</em>;
                        }
                        return <span key={j}>{part}</span>;
                      })}
                    </p>
                  );
                })}
              </div>

              {/* Data badge if search results */}
              {msg.hasData && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] px-2 py-1 rounded">
                      📊 {msg.resultCount} results
                    </span>
                    {msg.filters && Object.keys(msg.filters).length > 0 && (
                      <span className="text-white/60">
                        • Filtered by: {Object.keys(msg.filters).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="text-xs text-white/40 mt-2">
                {msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {/* Thinking indicator */}
        {isThinking && (
          <div className="flex justify-start">
            <div className="bg-white/10 border border-white/20 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[var(--pbfs-green)] rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">P</span>
                </div>
                <span className="text-white/60 text-sm">Pye is thinking</span>
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[var(--pbfs-green)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-[var(--pbfs-green)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-[var(--pbfs-green)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-[var(--pbfs-navy)]/80 border-t border-white/10">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Pye anything... (data search or just chat)"
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)]"
            disabled={isThinking}
          />
          <button
            onClick={handleSend}
            disabled={isThinking || !input.trim()}
            className="bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            {isThinking ? '...' : 'Send'}
          </button>
        </div>
        <p className="text-white/40 text-xs mt-2">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default PyeAIChat;

